import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "../../ComboFilters";
import classNames from "classnames/bind";
import {
  getAllMonthsUntilNow,
  getGrowthPercentage,
  getLastSixYears,
  months,
  Trend,
} from "../../Reports";
import { If } from "shared/components/control-statements";
import { ReportsAPI } from "providers/ReportsAPI";
import dayjs from "dayjs";
import { Loading } from "@components";
const css = classNames.bind(styles);

export const RFVProfilesChart = ({
  selectedData,
  onClose,
  context,
  startOn2025,
}) => {
  const getCurrentMonthYear = () => {
    const today = new Date();

    const date = new Date(today.getFullYear(), today.getMonth());
    const mes = months[date.getMonth()];
    const ano = date.getFullYear();

    return {
      name: `${mes} ${ano}`,
      id: String(`${date.getMonth() + 1}`).padStart(2, "0"),
    };
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = getCurrentMonthYear();

  const [data, setData] = useState({
    campeoes: [],
    promissores: [],
    requer_atencao: [],
    em_risco: [],
    desligando: [],
    hibernando: [],
    abandonados: [],
  });

  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });

  const daysInMonth = Array.from(
    {
      length: dayjs(
        `${selectedMonth.year}-${String(selectedMonth.id).padStart(2, "0")}`
      )
        .endOf("month")
        .date(),
    },
    (_, i) => i + 1
  );

  const formatData = () => {
    const today = dayjs();

    const result = {};

    if (selectedYear.id < today.year()) return data;

    const isYearFilter = selectedFilter === "year";
    const isCurrentYear = selectedYear.id === today.year();

    const isMonthFilter = selectedFilter === "month";
    const isCurrentMonth =
      selectedYear?.id === today.year() &&
      Number(selectedMonth?.id) === today.month() + 1;

    let limit = null;

    if (isYearFilter && isCurrentYear) limit = today.month();

    for (const key in data) {
      result[key] = data[key].map((value) => (value === 0 ? null : value));
    }

    if (isMonthFilter)
      if (isCurrentMonth) limit = today.date() - 2;
      else return result;

    for (const key in result) {
      result[key] =
        limit !== null ? result[key].slice(0, limit + 1) : result[key];
    }

    return result;
  };

  const series = formatData();

  const roundValue = (v) => {
    return Math.round(v / 10) * 10;
  };

  const getVisibleSeriesMin = (selected) => {
    let min = Infinity;
    let max = -Infinity;

    const normalizeLabel = (label) =>
      label
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

    for (const label in selected) {
      if (!selected[label]) continue;

      const key = normalizeLabel(label);
      const serieData = data[key];

      if (!serieData) continue;

      const values = serieData
        .map((d) => Number(d))
        .filter((v) => !isNaN(v) && v !== 0);

      if (values.length === 0) continue;

      const localMin = Math.min(...values);
      const localMax = Math.max(...values);
      min = Math.min(min, localMin);
      max = Math.max(max, localMax);
    }

    if (!isFinite(min)) return 0;

    const padding = (max - min) * 0.2;
    const ajustado = Math.max(0, Math.floor(min - padding));
    const rounded = roundValue(ajustado);

    return rounded;
  };

  const handleLegendSelectChanged = (e) => {
    const instance = echartsRef.current.getEchartsInstance();
    const selected = e.selected;

    const newMin = getVisibleSeriesMin(selected);

    instance.setOption({
      yAxis: {
        startValue: newMin,
      },
    });
  };

  const handleClick = (item) => {
    const instance = echartsRef.current.getEchartsInstance();
    instance.setOption({
      tooltip: {
        formatter: (params) => {
          const name = params[0].name;

          const everyNull = params.every((item) => item.data === null);
          if (everyNull) return;

          const highlightParams = params.filter(
            (p) => p.seriesName === item.seriesName
          );
          const otherParams = params.filter(
            (p) => p.seriesName !== item.seriesName
          );

          let lines;

          lines = highlightParams.map((item) => {
            if (!item.value) return null;
            return `${item.marker} ${item.seriesName}: <b>${(
              item.value || 0
            )?.toLocaleString("pt-BR")}</b><br/>`;
          });

          lines = lines.concat(
            otherParams.map((item) => {
              if (!item.value) return null;
              return `${item.marker} ${item.seriesName}: <b>${(
                item.value || 0
              )?.toLocaleString("pt-BR")}</b>`;
            })
          );

          const filteredLines = lines.filter((item) => item !== null);

          return `<div>
            <strong>${name}${
            selectedFilter === "month" ? ` de ${selectedMonth.name}` : ""
          }</strong><br/><br/>
            ${filteredLines.join("<br/>")}
          </div>`;
        },
      },
    });
  };

  const buildOptions = () => {
    return {
      grid: {
        left: 10,
        right: 30,
        top: 30,
        bottom: 70,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none",
        },
        backgroundColor: "#2B2B2B",
        borderRadius: 8,
        padding: [8, 8],
        borderWidth: 0,
        textStyle: {
          color: "#FFF",
          fontSize: 10,
        },
        formatter: (params) => {
          const name = params.name;

          if (params.data === null || params.value == null) return;

          const formattedValue = params.value.toLocaleString("pt-BR");

          return `
            <div>
              <strong>${name}${
            selectedFilter === "month" ? ` de ${selectedMonth.name}` : ""
          }</strong><br/><br/>
              ${params.marker} ${params.seriesName}: <b>${formattedValue}</b>
            </div>
          `;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        pageIconColor: "#6D6DF2",
        pageIconInactiveColor: "#4C4C4C",
        backgroundColor: "#1C1C1C",
        borderRadius: 10,
        padding: 20,
        pageIconSize: 10,
        pageTextStyle: {
          color: "#B3B3B3",
          fontSize: 10,
          fontWeight: 800,
        },
        pageIcons: {
          horizontal: [
            "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
            "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
          ],
        },
        top: "bottom",
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: {
          color: "#808080",
          fontSize: 12,
        },
        inactiveColor: "#555",
      },
      xAxis: {
        type: "category",
        data: selectedFilter === "year" ? months : Array.from(daysInMonth),
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 1,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        startValue: getVisibleSeriesMin(
          Object.keys(data).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        ),
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        axisLabel: {
          formatter: (value) => `${value.toLocaleString("pt-BR")}`,
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 0,
            type: "solid",
          },
        },
      },
      series: [
        {
          name: "Campeões",
          type: "line",
          data: series.campeoes,
          color: "#4bca97",
          symbolSize: 8,
          itemStyle: {
            color: "#4bca97",
            borderColor: "#4bca97",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#4bca97",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Promissores",
          type: "line",
          data: series.promissores,
          color: "#de52b4",
          symbolSize: 8,
          itemStyle: {
            color: "#de52b4",
            borderColor: "#de52b4",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#de52b4",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Requer atenção",
          type: "line",
          data: series.requer_atencao,
          color: "#d9d26c",
          symbolSize: 8,
          itemStyle: {
            color: "#d9d26c",
            borderColor: "#d9d26c",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#d9d26c",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Em risco",
          type: "line",
          data: series.em_risco,
          color: "#c74f53",
          symbolSize: 8,
          itemStyle: {
            color: "#c74f53",
            borderColor: "#c74f53",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#c74f53",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Desligando",
          type: "line",
          data: series.desligando,
          color: "#515d73",
          symbolSize: 8,
          itemStyle: {
            color: "#515d73",
            borderColor: "#515d73",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#515d73",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Hibernando",
          type: "line",
          data: series.hibernando,
          color: "#a67666",
          symbolSize: 8,
          itemStyle: {
            color: "#a67666",
            borderColor: "#a67666",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#a67666",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Abandonados",
          type: "line",
          data: series.abandonados,
          color: "#fff",
          symbolSize: 8,
          itemStyle: {
            color: "#fff",
            borderColor: "#fff",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#fff",
          },
          symbol: "circle",
          connectNulls: false,
        },
      ],
    };
  };

  const echartsRef = useRef(null);

  const [options, setOptions] = useState(buildOptions());

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  const fetchHistory = async (payload) => {
    setLoadingHistoric(true);
    const { year, month } = payload;
    const { brand, unit } = context || {};
    const provider = new ReportsAPI();
    const results = await provider.rfvHistory(
      "profile",
      year,
      month,
      brand?.id,
      unit?.id
    );

    const limit = month
      ? dayjs(`${year}-${month}`, "YYYY-MM").daysInMonth()
      : 12;

    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.date) continue;

      const format = month ? "DD/MM/YYYY" : "MM/YYYY";
      const date = dayjs(result.date, format);

      const index = month ? date.date() : date.month() + 1;

      if (index >= 1 && index <= limit) {
        rows[index - 1] = Object.fromEntries(
          Object.entries(result)
            .map(([key, obj]) => {
              if (key === "date") return null;
              return [key, Number(obj?.value ?? 0)];
            })
            .filter(Boolean)
        );
      }
    }

    setLoadingHistoric(false);
    return rows;
  };

  const fetchData = async () => {
    const payload = { year: selectedYear?.id };
    payload.month = selectedFilter === "month" ? selectedMonth?.id : null;

    const results = await fetchHistory(payload);

    const series = {
      campeoes: [],
      promissores: [],
      requer_atencao: [],
      em_risco: [],
      desligando: [],
      hibernando: [],
      abandonados: [],
    };

    results.forEach((result) => {
      for (const key in series) {
        if (result && result[key] !== undefined && result[key] !== null) {
          series[key].push(result[key]);
        } else {
          series[key].push(0);
        }
      }
    });

    setData(series);
  };

  useEffect(() => {
    fetchData();
  }, [selectedFilter, selectedYear, selectedMonth, context]);

  useEffect(() => {
    setOptions(buildOptions());
  }, [data]);

  return (
    <div className={styles.monthYearLegendChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={styles.monthYearLegendContent}>
        <If cond={loadingHistoric}>
          <div className={styles.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!loadingHistoric}>
          <div className={styles.legendContainer}>
            <div className={styles.header}>
              Crescimento no {selectedFilter === "year" ? "Ano" : "Mês"}
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#4bca97" }} />
                <p className={styles.name}>Campeões</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.campeoes,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#de52b4" }} />
                <p className={styles.name}>Promissores</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.promissores,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#d9d26c" }} />
                <p className={styles.name}>Requer atenção</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.requer_atencao,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#c74f53" }} />
                <p className={styles.name}>Em risco</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.em_risco,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#515d73" }} />
                <p className={styles.name}>Desligando</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.desligando,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#a67666" }} />
                <p className={styles.name}>Hibernando</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.hibernando,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#fff" }} />
                <p className={styles.name}>Abandonados</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.abandonados,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
          </div>

          <div className={styles.chartContainer}>
            <div className={styles.filters}>
              {/* <div className="d-flex align-center gap-10">
                <div
                  className={css({
                    selectMini: true,
                    selected: selectedFilter === "year",
                  })}
                  onClick={() => setSelectedFilter("year")}
                >
                  Ano
                </div>
                <div
                  className={css({
                    selectMini: true,
                    selected: selectedFilter === "month",
                  })}
                  onClick={() => setSelectedFilter("month")}
                >
                  Mês
                </div>
              </div> */}

              <div />

              <div className="d-flex align-center">
                <If cond={selectedFilter === "year"}>
                  <ComboFilters
                    options={getLastSixYears(startOn2025)}
                    onSelect={(option) => setSelectedYear(option)}
                    selectedOption={selectedYear}
                  />
                </If>
                <If cond={selectedFilter === "month"}>
                  <ComboFilters
                    options={getAllMonthsUntilNow()}
                    onSelect={(option) => setSelectedMonth(option)}
                    selectedOption={selectedMonth}
                  />
                </If>
              </div>
            </div>
            <div className={styles.monthYearChart}>
              <ReactECharts
                ref={echartsRef}
                option={options}
                style={{ width: "100%", height: "100%" }}
                notMerge={true}
                lazyUpdate={true}
                opts={{ renderer: "canvas" }}
                onEvents={{
                  legendselectchanged: handleLegendSelectChanged,
                  // mouseover: handleClick,
                }}
              />
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};
